import { Box, Icon, SimpleGrid, useColorModeValue } from "@chakra-ui/react";
import axios from "axios";
import MiniStatistics from "../../../components/admindashboard/card/MiniStatistics";
import IconBox from "../../../components/admindashboard/icons/IconBox";
import React, { useEffect, useMemo, useState } from "react";
import {
  MdAddTask,
  MdAttachMoney,
  MdBarChart,
  MdFileCopy,
} from "react-icons/md";
import CheckTable from "../../../views/admin/default/components/CheckTable";
import DailyTraffic from "../../../views/admin/default/components/DailyTraffic";
import PieCard from "../../../views/admin/default/components/PieCard";
import TotalSpent from "../../../views/admin/default/components/TotalSpent";
import WeeklyRevenue from "../../../views/admin/default/components/WeeklyRevenue";
import {
  barChartOptionsConsumption,
  barChartOptionsDailyTraffic,
  lineChartOptionsTotalSpent,
  pieChartOptions,
} from "variables/charts";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API_BASE = `${ENV_API_BASE_URL}/api`;

const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
const dayFormatter = new Intl.DateTimeFormat("en-US", { weekday: "short" });
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const getLastMonths = (count) => {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (count - 1 - index), 1);
    return {
      key: `${date.getFullYear()}-${date.getMonth()}`,
      label: monthFormatter.format(date).toUpperCase(),
      date,
    };
  });
};

const getLastDays = (count, offset = 0) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - offset - (count - 1 - index));
    return {
      key: date.toISOString().slice(0, 10),
      label: dayFormatter.format(date),
      date,
    };
  });
};

const safeDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatCompactNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value || 0);

const formatPercentChange = (current, previous) => {
  if (!previous) return current > 0 ? "+100%" : "0%";
  const change = ((current - previous) / previous) * 100;
  const rounded = Math.abs(change) < 0.05 ? 0 : change;
  return `${rounded >= 0 ? "+" : ""}${rounded.toFixed(1)}%`;
};

const capitalize = (value = "") =>
  String(value).charAt(0).toUpperCase() + String(value).slice(1);

const createInitialDashboard = () => ({
  stats: {
    courses: 0,
    batches: 0,
    blogs: 0,
    bookings: 0,
    confirmedBookings: 0,
    reservedSeats: 0,
  },
  bookingsOverview: {
    summaryValue: "0",
    growthValue: "0%",
    growthPositive: true,
    statusText: "No bookings yet",
    chartData: [
      { name: "Bookings", data: [0, 0, 0, 0, 0, 0] },
      { name: "Confirmed", data: [0, 0, 0, 0, 0, 0] },
    ],
    chartOptions: {
      ...lineChartOptionsTotalSpent,
      xaxis: {
        ...lineChartOptionsTotalSpent.xaxis,
        categories: getLastMonths(6).map((item) => item.label),
      },
    },
  },
  topCourses: {
    chartData: [{ name: "Bookings", data: [0] }],
    chartOptions: {
      ...barChartOptionsConsumption,
      chart: { ...barChartOptionsConsumption.chart, stacked: false },
      colors: ["#4318FF"],
      fill: { type: "solid", colors: ["#4318FF"] },
      xaxis: {
        ...barChartOptionsConsumption.xaxis,
        categories: ["No data"],
      },
    },
  },
  dailyInquiries: {
    value: "0",
    growthValue: "0%",
    growthPositive: true,
    chartData: [{ name: "Inquiries", data: [0, 0, 0, 0, 0, 0, 0] }],
    chartOptions: {
      ...barChartOptionsDailyTraffic,
      xaxis: {
        ...barChartOptionsDailyTraffic.xaxis,
        categories: getLastDays(7).map((item) => item.label),
      },
    },
  },
  bookingStatus: {
    chartData: [0, 0, 0],
    chartOptions: {
      ...pieChartOptions,
      labels: ["Confirmed", "Pending", "Cancelled"],
      colors: ["#4318FF", "#6AD2FF", "#EFF4FB"],
      fill: {
        ...pieChartOptions.fill,
        colors: ["#4318FF", "#6AD2FF", "#EFF4FB"],
      },
    },
    legendItems: [
      { label: "Confirmed", value: "0%", color: "#4318FF" },
      { label: "Pending", value: "0%", color: "#6AD2FF" },
      { label: "Cancelled", value: "0%", color: "#EFF4FB" },
    ],
  },
  recentBookings: [],
});

const buildDashboardData = ({ courses, batches, bookings, blogs }) => {
  const confirmedBookings = bookings.filter((item) => item.status === "confirmed");
  const pendingBookings = bookings.filter((item) => item.status === "pending");
  const cancelledBookings = bookings.filter((item) => item.status === "cancelled");
  const reservedSeats = bookings
    .filter((item) => item.status !== "cancelled")
    .reduce((sum, item) => sum + Number(item.seats || 1), 0);

  const monthlyBuckets = getLastMonths(6).map((bucket) => ({
    ...bucket,
    total: 0,
    confirmed: 0,
  }));

  bookings.forEach((booking) => {
    const createdAt = safeDate(booking.createdAt);
    if (!createdAt) return;
    const key = `${createdAt.getFullYear()}-${createdAt.getMonth()}`;
    const targetBucket = monthlyBuckets.find((bucket) => bucket.key === key);
    if (!targetBucket) return;
    targetBucket.total += 1;
    if (booking.status === "confirmed") {
      targetBucket.confirmed += 1;
    }
  });

  const currentMonthTotal = monthlyBuckets[monthlyBuckets.length - 1]?.total || 0;
  const previousMonthTotal = monthlyBuckets[monthlyBuckets.length - 2]?.total || 0;

  const dailyBuckets = getLastDays(7).map((bucket) => ({
    ...bucket,
    count: 0,
  }));
  const previousDailyBuckets = getLastDays(7, 7).map((bucket) => ({
    ...bucket,
    count: 0,
  }));

  bookings.forEach((booking) => {
    const createdAt = safeDate(booking.createdAt);
    if (!createdAt) return;
    const key = createdAt.toISOString().slice(0, 10);
    const dailyBucket = dailyBuckets.find((bucket) => bucket.key === key);
    if (dailyBucket) dailyBucket.count += 1;
    const previousBucket = previousDailyBuckets.find((bucket) => bucket.key === key);
    if (previousBucket) previousBucket.count += 1;
  });

  const currentWeekTotal = dailyBuckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const previousWeekTotal = previousDailyBuckets.reduce((sum, bucket) => sum + bucket.count, 0);

  const courseBookingMap = new Map();
  bookings.forEach((booking) => {
    const label =
      booking.course?.shortTitle ||
      booking.course?.title ||
      booking.course?.slug ||
      "Unknown Course";
    courseBookingMap.set(label, (courseBookingMap.get(label) || 0) + 1);
  });

  const topCourses = [...courseBookingMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const totalBookings = bookings.length || 1;
  const statusLegendItems = [
    { label: "Confirmed", value: confirmedBookings.length, color: "#4318FF" },
    { label: "Pending", value: pendingBookings.length, color: "#6AD2FF" },
    { label: "Cancelled", value: cancelledBookings.length, color: "#EFF4FB" },
  ].map((item) => ({
    ...item,
    value: `${Math.round((item.value / totalBookings) * 100)}%`,
  }));

  return {
    stats: {
      courses: courses.length,
      batches: batches.length,
      blogs: blogs.length,
      bookings: bookings.length,
      confirmedBookings: confirmedBookings.length,
      reservedSeats,
    },
    bookingsOverview: {
      summaryValue: formatCompactNumber(currentMonthTotal),
      growthValue: formatPercentChange(currentMonthTotal, previousMonthTotal),
      growthPositive: currentMonthTotal >= previousMonthTotal,
      statusText:
        bookings.length > 0
          ? `${confirmedBookings.length} confirmed bookings total`
          : "No bookings yet",
      chartData: [
        {
          name: "Bookings",
          data: monthlyBuckets.map((bucket) => bucket.total),
        },
        {
          name: "Confirmed",
          data: monthlyBuckets.map((bucket) => bucket.confirmed),
        },
      ],
      chartOptions: {
        ...lineChartOptionsTotalSpent,
        xaxis: {
          ...lineChartOptionsTotalSpent.xaxis,
          categories: monthlyBuckets.map((bucket) => bucket.label),
        },
      },
    },
    topCourses: {
      chartData: [
        {
          name: "Bookings",
          data: (topCourses.length ? topCourses : [["No data", 0]]).map((item) => item[1]),
        },
      ],
      chartOptions: {
        ...barChartOptionsConsumption,
        chart: {
          ...barChartOptionsConsumption.chart,
          stacked: false,
        },
        colors: ["#4318FF"],
        fill: {
          type: "solid",
          colors: ["#4318FF"],
        },
        xaxis: {
          ...barChartOptionsConsumption.xaxis,
          categories: (topCourses.length ? topCourses : [["No data", 0]]).map((item) => item[0]),
          labels: {
            ...barChartOptionsConsumption.xaxis.labels,
            rotate: -25,
            trim: true,
          },
        },
        plotOptions: {
          ...barChartOptionsConsumption.plotOptions,
          bar: {
            ...barChartOptionsConsumption.plotOptions.bar,
            columnWidth: "28px",
          },
        },
      },
    },
    dailyInquiries: {
      value: formatCompactNumber(currentWeekTotal),
      growthValue: formatPercentChange(currentWeekTotal, previousWeekTotal),
      growthPositive: currentWeekTotal >= previousWeekTotal,
      chartData: [
        {
          name: "Inquiries",
          data: dailyBuckets.map((bucket) => bucket.count),
        },
      ],
      chartOptions: {
        ...barChartOptionsDailyTraffic,
        xaxis: {
          ...barChartOptionsDailyTraffic.xaxis,
          categories: dailyBuckets.map((bucket) => bucket.label),
        },
      },
    },
    bookingStatus: {
      chartData: [
        confirmedBookings.length,
        pendingBookings.length,
        cancelledBookings.length,
      ],
      chartOptions: {
        ...pieChartOptions,
        labels: ["Confirmed", "Pending", "Cancelled"],
        colors: ["#4318FF", "#6AD2FF", "#EFF4FB"],
        fill: {
          ...pieChartOptions.fill,
          colors: ["#4318FF", "#6AD2FF", "#EFF4FB"],
        },
      },
      legendItems: statusLegendItems,
    },
    recentBookings: bookings.slice(0, 5).map((booking) => ({
      name: [booking.name || booking.email || "Unknown", booking.status === "confirmed"],
      progress: capitalize(booking.status || "pending"),
      quantity: `${Number(booking.seats || 1)} seat${Number(booking.seats || 1) > 1 ? "s" : ""}`,
      date: safeDate(booking.createdAt)
        ? dateFormatter.format(safeDate(booking.createdAt))
        : "Unknown date",
    })),
  };
};

export default function UserReports() {
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const [dashboard, setDashboard] = useState(createInitialDashboard);

  useEffect(() => {
    let active = true;

    const fetchDashboardData = async () => {
      const [coursesRes, batchesRes, bookingsRes, blogsRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/courses`),
        axios.get(`${API_BASE}/course-batches`),
        axios.get(`${API_BASE}/course-bookings`),
        axios.get(`${API_BASE}/blog-details`),
      ]);

      if (!active) return;

      const data = {
        courses:
          coursesRes.status === "fulfilled" ? coursesRes.value.data?.data || [] : [],
        batches:
          batchesRes.status === "fulfilled" ? batchesRes.value.data?.data || [] : [],
        bookings:
          bookingsRes.status === "fulfilled" ? bookingsRes.value.data?.data || [] : [],
        blogs:
          blogsRes.status === "fulfilled" ? blogsRes.value.data?.data || [] : [],
      };

      setDashboard(buildDashboardData(data));
    };

    const refreshDashboard = () => {
      fetchDashboardData().catch(() => {
        if (active) {
          setDashboard(createInitialDashboard());
        }
      });
    };

    const handleWindowFocus = () => {
      if (document.visibilityState === "visible") {
        refreshDashboard();
      }
    };

    const intervalId = window.setInterval(refreshDashboard, 20000);

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleWindowFocus);

    refreshDashboard();

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleWindowFocus);
    };
  }, []);

  const statCards = useMemo(
    () => [
      {
        name: "Courses",
        value: dashboard.stats.courses,
        icon: MdBarChart,
        bg: boxBg,
        color: brandColor,
      },
      {
        name: "Course Batches",
        value: dashboard.stats.batches,
        icon: MdAttachMoney,
        bg: boxBg,
        color: brandColor,
      },
      {
        name: "Blog Posts",
        value: dashboard.stats.blogs,
        icon: MdFileCopy,
        bg: boxBg,
        color: brandColor,
      },
      {
        name: "Total Bookings",
        value: dashboard.stats.bookings,
        icon: MdAddTask,
        bg: "linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)",
        color: "white",
      },
      {
        name: "Confirmed",
        value: dashboard.stats.confirmedBookings,
        icon: MdAddTask,
        bg: boxBg,
        color: brandColor,
      },
      {
        name: "Reserved Seats",
        value: dashboard.stats.reservedSeats,
        icon: MdFileCopy,
        bg: boxBg,
        color: brandColor,
      },
    ],
    [boxBg, brandColor, dashboard.stats]
  );

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
        gap='20px'
        mb='20px'>
        {statCards.map((item) => (
          <MiniStatistics
            key={item.name}
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={item.bg}
                icon={<Icon w='32px' h='32px' as={item.icon} color={item.color} />}
              />
            }
            name={item.name}
            value={String(item.value)}
          />
        ))}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px' mb='20px'>
        <TotalSpent
          title='Bookings This Month'
          filterLabel='Last 6 months'
          summaryValue={dashboard.bookingsOverview.summaryValue}
          growthValue={dashboard.bookingsOverview.growthValue}
          growthPositive={dashboard.bookingsOverview.growthPositive}
          statusText={dashboard.bookingsOverview.statusText}
          chartData={dashboard.bookingsOverview.chartData}
          chartOptions={dashboard.bookingsOverview.chartOptions}
        />
        <WeeklyRevenue
          title='Top Courses by Bookings'
          chartData={dashboard.topCourses.chartData}
          chartOptions={dashboard.topCourses.chartOptions}
        />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
        <CheckTable title='Recent Bookings' tableData={dashboard.recentBookings} />
        <SimpleGrid columns={1} gap='20px'>
          <DailyTraffic
            title='Last 7 Days'
            value={dashboard.dailyInquiries.value}
            label='Inquiries'
            growthValue={dashboard.dailyInquiries.growthValue}
            growthPositive={dashboard.dailyInquiries.growthPositive}
            chartData={dashboard.dailyInquiries.chartData}
            chartOptions={dashboard.dailyInquiries.chartOptions}
          />
          <PieCard
            title='Booking Status'
            chartData={dashboard.bookingStatus.chartData}
            chartOptions={dashboard.bookingStatus.chartOptions}
            legendItems={dashboard.bookingStatus.legendItems}
          />
        </SimpleGrid>
      </SimpleGrid>
    </Box>
  );
}
